import {Component, Input} from '@angular/core';
import {HandlingOriginEnum} from "../../../../../_core/enum/handling-origin.enum";
import {ModuleCreationRequestResult} from "../../../../../_core/controllers/item.controller";
import {Handling} from "../../../../../_core/classes/handling";
import { ModuleCreationRequestParameter } from 'projects/ct-manager/src/app/_core/classes/module-creation-request-parameter';
import { OriginCreateWorkOrderEnum, TransferItemsRequestParameter } from 'projects/ct-manager/src/app/_core/classes/transfer-items-request-parameter';

@Component({
  selector: 'app-module-create-handlings',
  templateUrl: './module-create-handlings.component.html',
  styleUrls: ['./module-create-handlings.component.scss']
})
export class ModuleCreateHandlingsComponent {
  @Input() model: ModuleCreationRequestResult | null = null;
  @Input() parameter: ModuleCreationRequestParameter | TransferItemsRequestParameter | null = null;

  // Getter per le movimentazioni tra depositi
  get handlings(): Handling[] {
    return this.getItemHandlingsByType(HandlingOriginEnum.Location);
  }

  // Getter per gli ordini ai fornitori
  get orders(): Handling[] {
    return this.getItemHandlingsByType(HandlingOriginEnum.Supplier);
  }

  /**
   * Determina se l'origine è una Work Order (escludendo i moduli)
   * @returns true se l'origine è WorkOrder o Handling, false altrimenti (Module)
   */
  get isWorkOrderOrigin(): boolean {
    if (!this.parameter?.Origin) return false;
    
    return this.parameter.Origin === OriginCreateWorkOrderEnum.WorkOrder || 
           this.parameter.Origin === OriginCreateWorkOrderEnum.Handling;
  }

  /**
   * Restituisce il titolo appropriato per la sezione movimentazioni
   */
  getMovementTitle(): string {
    return this.isWorkOrderOrigin ? 
      'WorkOrderHandlings.Movement.Title' : 
      'ModuleCreateHandlings.MovementGenerationMessage';
  }

  /**
   * Restituisce il messaggio appropriato per la sezione movimentazioni
   */
  getMovementMessage(): string {
    return this.isWorkOrderOrigin ? 
      'WorkOrderHandlings.Movement.Message' : 
      'ModuleCreateHandlings.MovementGenerationMessage';
  }

  /**
   * Restituisce il titolo appropriato per la sezione ordini
   */
  getOrderTitle(): string {
    if (!this.isWorkOrderOrigin) {
      return 'ModuleCreateHandlings.OrderGenerationMessage';
    }

    // Per Work Order: distinguiamo tra fornitore singolo e multiplo
    return this.hasOnlyOneSupplier() ? 
      'WorkOrderHandlings.Order.Single.Title' : 
      'WorkOrderHandlings.Order.Multiple.Title';
  }

  /**
   * Restituisce il messaggio appropriato per la sezione ordini
   */
  getOrderMessage(): string {
    if (!this.isWorkOrderOrigin) {
      return 'ModuleCreateHandlings.OrderGenerationMessage';
    }

    return this.hasOnlyOneSupplier() ? 
      'WorkOrderHandlings.Order.Single.Message' : 
      'WorkOrderHandlings.Order.Multiple.Message';
  }

  /**
   * Restituisce i parametri per il messaggio degli ordini (es. nome fornitore)
   */
  getOrderMessageParams(): any {
    if (this.isWorkOrderOrigin && this.hasOnlyOneSupplier()) {
      return { supplierName: this.getSingleSupplierName() };
    }
    return {};
  }

  /**
   * Verifica se c'è un solo fornitore coinvolto negli ordini
   */
  hasOnlyOneSupplier(): boolean {
    const uniqueSuppliers = this.getUniqueSuppliers();
    return uniqueSuppliers.length === 1;
  }

  /**
   * Verifica se ci sono più fornitori coinvolti negli ordini
   */
  hasMultipleSuppliers(): boolean {
    const uniqueSuppliers = this.getUniqueSuppliers();
    return uniqueSuppliers.length > 1;
  }

  /**
   * Restituisce il nome del fornitore se ce n'è uno solo
   */
  getSingleSupplierName(): string {
    const suppliers = this.getUniqueSuppliers();
    return suppliers.length === 1 ? (suppliers[0].ReferenceName || 'Non specificato') : '';
  }

  /**
   * Restituisce il messaggio di conferma appropriato e specifico
   */
  getConfirmMessage(): string {
    if (!this.isWorkOrderOrigin) {
      return 'confirmProceedMessage';
    }

    const hasMovements = this.handlings.length > 0;
    const hasOrders = this.orders.length > 0;

    // Caso: Solo movimentazioni
    if (hasMovements && !hasOrders) {
      return 'WorkOrderHandlings.Confirm.Movement';
    }

    // Caso: Solo ordini
    if (!hasMovements && hasOrders) {
      const supplierCount = this.getUniqueSuppliers().length;
      return supplierCount === 1 ? 
        'WorkOrderHandlings.Confirm.Order' : 
        'WorkOrderHandlings.Confirm.Orders';
    }

    // Caso: Mix di movimentazioni e ordini
    if (hasMovements && hasOrders) {
      return 'WorkOrderHandlings.Confirm.Mixed';
    }

    return 'WorkOrderHandlings.Confirm.Message';
  }

  /**
   * Verifica se mostrare la nota per i moduli
   */
  shouldShowModuleNote(): boolean {
    // Mostra la nota solo per i moduli (Origin === 3)
    return this.parameter?.Origin === OriginCreateWorkOrderEnum.Module;
  }

  /**
   * Estrae l'elenco di fornitori unici dagli ordini
   * @returns Array di fornitori unici
   */
  private getUniqueSuppliers(): any[] {
    const suppliersMap = new Map();
    
    this.orders.forEach(order => {
      if (order.SupplierFrom?.Oid) {
        suppliersMap.set(order.SupplierFrom.Oid, order.SupplierFrom);
      }
    });

    return Array.from(suppliersMap.values());
  }

  /**
   * Filtra gli handling per tipo (Location o Supplier)
   */
  private getItemHandlingsByType(handlingOrigin: HandlingOriginEnum): Handling[] {
    return (this.model?.Handlings ?? [])
      .filter(handling => handling.HandlingOrigin === handlingOrigin);
  }

}
